import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, X, ChevronDown, ChevronUp, Lightbulb, FileText, Download, BookOpen, Minimize2 } from 'lucide-react';
import jsPDF from 'jspdf';
import { ComfortaaFont } from './ComfortaaFont';

export default function SWOTAnalysisApp() {
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [showEditor, setShowEditor] = useState(false);
  const [selectedEditorQuadrant, setSelectedEditorQuadrant] = useState('strengths');
  const [swotAnswers, setSwotAnswers] = useState({});
  const [hiddenQuestions, setHiddenQuestions] = useState({});
  const [showGuide, setShowGuide] = useState(false);
  const [isEditorMinimized, setIsEditorMinimized] = useState(false);
  const [isGuideAudioPlaying, setIsGuideAudioPlaying] = useState(false);
  const [guideAudioProgress, setGuideAudioProgress] = useState(0);
  const [guideAudioDuration, setGuideAudioDuration] = useState(0);
  const audioRefs = useRef([]);
  const guideAudioRef = useRef(null);

  // SWOT Quadrants adatok a részletes tananyag alapján
  const quadrants = [
    {
      id: 'strengths',
      title: 'ERŐSSÉGEK',
      icon: '💪',
      color: '#7FA893',
      gradient: 'from-[#7FA893] to-[#6B9080]',
      description: 'Belső, pozitív tulajdonságok, képességek, erőforrások és versenyelőnyök',
      explanation: 'Az erősségek azok a belső, pozitív tulajdonságok, képességek, erőforrások és versenyelőnyök, amelyek segítenek céljaink elérésében.',
      audioUrl: '/audio/strengths.mp3',
      categories: [
        {
          name: 'Alapvető Erősség-Kérdések',
          questions: [
            'Mit csinálunk kiemelkedően jól?',
            'Milyen egyedi erőforrásokkal vagy eszközökkel rendelkezünk?',
            'Mik a fő kompetenciáink?',
            'Mi a versenyelőnyünk?',
            'Mi az egyedi értékajánlatunk (Unique Selling Point)?',
            'Mire ismernek bennünket az emberek?',
            'Milyen pozitív tulajdonságokat társítanak márkánkhoz / személyiségünkhöz?',
            'Mekkora a piaci részesedésünk?',
            'Milyen erős ügyfélkapcsolatokat ápolunk?',
            'Milyen múltbeli eredményeket tudunk felhasználni?'
          ]
        },
        {
          name: 'Erőforrás-Központú Kérdések',
          questions: [
            'Milyen a pénzügyi helyzetünk?',
            'Mik a munkatársaink kulcsfontosságú erősségei?',
            'Mik a saját kulcsfontosságú erősségeim?',
            'Milyen technológiákkal vagy szellemi tulajdonnal rendelkezünk?',
            'Milyen pozitív piaci trendeket használtunk ki sikeresen?',
            'Milyen értékes partnerségekkel vagy szövetségekkel rendelkezünk?',
            'Milyen pozitív felhasználói/kollegiális visszajelzéseket kaptunk?',
            'Milyen eszközök vagy tulajdonságok segítenek üzletet szerezni?',
            'Milyen eszközök vagy tulajdonságok segítenek ügyfeleket megtartani és bővíteni?'
          ]
        },
        {
          name: 'Mély Önismereti Kérdések',
          questions: [
            'Milyen előnyeim vannak a konkurenciával / másokkal szemben?',
            'Mi az, amit rendkívül jól csinálok?',
            'Milyen természetes tehetségeim vannak?',
            'Milyen készségeket sajátítottam el könnyedén?',
            'Mikor vagyok a legjobb formámban?',
            'Mi az, amit mások értékelnek bennem?',
            'Milyen pozitív visszajelzéseket kaptam az elmúlt évben?',
            'Milyen erősségeim segítettek sikeres lenni korábban?',
            'Milyen alapvető értékeket testesítek meg jól?',
            'Nyugodt vagyok-e nyomás alatt?',
            'Aktív hallgató vagyok-e?',
            'Jó vezető vagyok-e?'
          ]
        },
        {
          name: 'Tapasztalat és Tudás',
          questions: [
            'Milyen releváns tapasztalatokkal rendelkezem?',
            'Milyen szakmai képesítéseim vagy végzettségeim vannak?',
            'Milyen hard skills-ekkel (technikai tudás) rendelkezem?',
            'Milyen soft skills-ekkel (kommunikáció, vezetés, empátia) rendelkezem?',
            'Milyen speciális szakterületeken vagyok szakértő?',
            'Milyen nyelveket beszélek?',
            'Milyen kulturális kompetenciával rendelkezem?'
          ]
        },
        {
          name: 'Személyiség és Attitűd',
          questions: [
            'Kitartó vagyok-e nehézségek ellenére?',
            'Rugalmasan alkalmazkodom-e a változásokhoz?',
            'Pozitív hozzáállással rendelkezem-e?',
            'Kreatív problémamegoldó vagyok-e?',
            'Jól kezelem-e a stresszt?',
            'Önmotiváló vagyok-e?',
            'Empátiás vagyok-e mások iránt?'
          ]
        },
        {
          name: 'Kapcsolatok és Háló',
          questions: [
            'Milyen erős szakmai hálóval rendelkezem?',
            'Kikhez fordulhatok tanácsért vagy segítségért?',
            'Milyen kapcsolataim vannak, amelyek előmozdíthatják céljaimat?',
            'Milyen közösségekben vagyok aktív tag?',
            'Mennyire széleskörű a kapcsolati hálóm?'
          ]
        }
      ]
    },
    {
      id: 'weaknesses',
      title: 'GYENGESÉGEK',
      icon: '🎯',
      color: '#9CA3AF',
      gradient: 'from-gray-400 to-gray-500',
      description: 'Belső, negatív tényezők, amelyek hátráltatnak céjaim elérésében',
      explanation: 'A gyengeségek azok a belső, negatív tényezők, amelyek hátráltatnak céljaim elérésében vagy hátrányt jelentenek másokhoz képest.',
      audioUrl: '/audio/weaknesses.mp3',
      categories: [
        {
          name: 'Alapvető Gyengeség-Kérdések',
          questions: [
            'Mik a fejlesztendő területeim?',
            'Mit csinálunk rosszul vagy ineffektíven?',
            'Mivel küzdünk vállalkozásként / csapatként / egyénileg?',
            'Hol pazaroljuk a pénzt?',
            'Melyek azok a gyakorlatok vagy cselekvések, amelyek negatív vagy túl kicsi megtérülést hoznak?',
            'Milyen konkrét visszajelzéseket kaptunk felhasználóktól vagy ügyfelektől, amelyek gyengeségeket jeleznek?',
            'Milyen erőforrásokból hiányozunk?',
            'Mik a versenyhátránnyaink?',
            'Van-e negatív tulajdonság, amit márkánkhoz / személyiségemhez társítanak?',
            'Hol küzdünk a piaci részesedés megszerzésében?'
          ]
        },
        {
          name: 'Készség és Kompetencia Hiányok',
          questions: [
            'Milyen készségekből hiányozom?',
            'Milyen szakmai ismereteim korlátozottak?',
            'Milyen technikai képességeim hiányosak?',
            'Milyen soft skill területeken fejlődnöm kellene?',
            'Milyen képzettségek vagy tanúsítványok hiányoznak?',
            'Van-e olyan terület, ahol másik tudása jóval megelőzi az enyémet?'
          ]
        },
        {
          name: 'Viselkedési és Szokás Problémák',
          questions: [
            'Hajlamos vagyok-e a halogatásra (prokrasztináció)?',
            'Rossz az időgazdálkodásom?',
            'Túlzottan perfekcionista vagyok-e, ami lassít?',
            'Kerülöm-e a konfliktusokat egészségtelen módon?',
            'Nehezen mondok-e nemet?',
            'Könnyen elterelődök-e?',
            'Impulzív döntéseket hozok-e?'
          ]
        },
        {
          name: 'Érzelmi és Pszichológiai Korlátok',
          questions: [
            'Szorongok-e bizonyos helyzetekben?',
            'Hiányzik-e az önbizalmam bizonyos területeken?',
            'Túl kritikus vagyok-e önmagammal szemben?',
            'Félelem gátol-e meg bizonyos lépéseket?',
            'Túl aggódom-e mások véleményén?',
            'Ellenállok-e a változásoknak?'
          ]
        },
        {
          name: 'Erőforrás és Kapacitás Korlátok',
          questions: [
            'Milyen pénzügyi korlátaim vannak?',
            'Nincs elég időm?',
            'Korlátozott-e a kapcsolati hálóm?',
            'Hiányoznak-e az eszközök, amiket használnom kellene?',
            'Elegendő energiám van-e folyamatosan?',
            'Elérhető vagyok-e megfelelő helyen földrajzilag?'
          ]
        },
        {
          name: 'Múltbeli Minták és Tapasztalatok',
          questions: [
            'Milyen múltbeli hibákat vagy kudarcokat tapasztaltam?',
            'Milyen ügyfélpanaszok vagy problémák ismétlődnek?',
            'Mi okozza az ügyfél/felhasználó lemorzsolódást?',
            'Milyen negatív trendek hatnak ránk a piacon?',
            'Milyen pénzügyi sebezhetőségeink vannak?'
          ]
        },
        {
          name: 'Technikai és Operációs Gyengeségek',
          questions: [
            'Milyen készséghiányok vannak a munkatársaink körében?',
            'Milyen elavult technológiákat vagy folyamatokat használunk?',
            'Milyen sebezhetőségek vannak szellemi tulajdonunkban?',
            'Milyen konfliktusok vagy problémák vannak partnereinkkel vagy szövetségeseinkkel?',
            'Mit nem szeretek csinálni?'
          ]
        },
        {
          name: 'Egészség és Jóllét',
          questions: [
            'Van-e olyan egészségügyi problémám, ami korlátozzon?',
            'Hogyan kezelek a stresszt és a kiégést?',
            'Elegendő alvást kapok-e?',
            'Megfelelően táplálkozom-e?',
            'Rendszeresen mozgok-e?'
          ]
        }
      ]
    },
    {
      id: 'opportunities',
      title: 'LEHETŐSÉGEK',
      icon: '🚀',
      color: '#7FA893',
      gradient: 'from-[#7FA893] to-[#6B9080]',
      description: 'Külső, pozitív tényezők és körülmények, amelyek előnyt jelenthetnek',
      explanation: 'A lehetőségek azok a külső, pozitív tényezők és körülmények, amelyeket kihasználva előnyünk származhat vagy céljainkhoz közelebb kerülhetünk.',
      audioUrl: '/audio/opportunities.mp3',
      categories: [
        {
          name: 'Alapvető Lehetőség-Kérdések',
          questions: [
            'Milyen feltörekvő piaci trendeket vagy változásokat használhatnánk ki?',
            'Vannak-e feltáratlan niche-ek vagy szegmensek a piacon?',
            'Milyen erőforrásaink vannak, amelyeket jelenleg nem használunk ki teljes mértékben?',
            'Milyen feltörekvő technológiák hasznos lehetnek számunkra?',
            'Vannak-e nem kielégített ügyfelkaptuk igények, amelyeket ki tudnánk elégíteni?',
            'Milyen funkciókat/fejlesztéseket kérnek az ügyfelek?',
            'Milyen funkciók/fejlesztések lennének a legnagyobb hatással a bevételre/elfogadásra?',
            'Vannak-e olyan szabályozási vagy politikai változások, amelyek lehetőségeket teremthetnek?'
          ]
        },
        {
          name: 'Növekedési és Expanziós Lehetőségek',
          questions: [
            'Bővíthetjük-e termék- vagy szolgáltatási kínálatunkat?',
            'Vannak-e új földrajzi piacok, amelyekre beléphetünk?',
            'Vannak-e kihasználatlan értékesítési csatornák, amelyeket folytathatnánk?',
            'Felhasználhatjuk-e márkareputációnkat új módon?',
            'Vannak-e kiegészítő vállalkozások, amelyekkel partneri kapcsolatot építhetnénk?',
            'Mi a növekedési potenciál az iparágunkban?',
            'Vannak-e új termékvertikálok, amelyeket tesztelh etnénk?',
            'Diverzifikálhatunk-e kapcsolódó iparágakba?'
          ]
        },
        {
          name: 'Hatékonyság és Optimalizálás',
          questions: [
            'Vannak-e lehetőségek költségcsökkentésre vagy hatékonyság-növelésre?',
            'Bevezethetünk-e új marketing vagy értékesítési stratégiákat?',
            'Milyen kísérleteket kellene végeznünk a lehetőségek validálásához?'
          ]
        },
        {
          name: 'Személyes Fejlődési Lehetőségek',
          questions: [
            'Milyen új készségeket tanulhatnék meg, amelyek előnyöket hoznának?',
            'Vannak-e elérhető képzési vagy fejlesztési programok?',
            'Milyen mentorok vagy coach-ok segíthetnének?',
            'Vannak-e szakmai hálózatok vagy szervezetek, amelyekhez csatlakozhatnék?',
            'Milyen konferenciák vagy események nyújtanának értéket?'
          ]
        },
        {
          name: 'Élethelyzet és Környezet Változások',
          questions: [
            'Vannak-e személyes élethelyzet változások, amelyek új lehetőségeket teremtenek? (pl. gyerekek elköltözése, nyugdíjba vonulás, örökség)',
            'Költözhetünk-e olyan helyre, ahol több lehetőség van?',
            'Vannak-e új kapcsolatok vagy partnerségek, amelyek ajtókat nyithatnak?',
            'Milyen időbeli változások adnak több szabadságot?'
          ]
        },
        {
          name: 'Piaci és Versenyzői Lehetőségek',
          questions: [
            'Milyen rések vagy hiányosságok vannak a konkurenseknél, amelyeket kihasználhatnánk?',
            'Vannak-e olyan területek, ahol a konkurensek gyengék?',
            'Milyen igények vannak a piacon, amelyeket senki sem elégít ki megfelelően?',
            'Milyen változások történnek a vásárlói preferenciákban, amelyek nekünk kedveznek?'
          ]
        },
        {
          name: 'Technológiai és Innovációs Lehetőségek',
          questions: [
            'Milyen új platformok vagy eszközök segíthetnének?',
            'Van-e lehetőség automatizációra vagy digitalizációra?',
            'Milyen adatokat vagy insights-okat használhatnánk jobban ki?',
            'Vannak-e AI vagy gépi tanulás alkalmazások, amelyek segíthetnének?'
          ]
        },
        {
          name: 'Pénzügyi és Forrásszerzési Lehetőségek',
          questions: [
            'Vannak-e elérhető támogatási programok vagy pályázatok?',
            'Vannak-e befektetési lehetőségek?',
            'Vannak-e adókedvezmények vagy ösztönzők, amelyeket kihasználhatnánk?',
            'Vannak-e crowdfunding vagy alternatív finanszírozási módok?'
          ]
        },
        {
          name: 'Társadalmi és Kulturális Trendek',
          questions: [
            'Milyen demográfiai változások előnyösek számunkra?',
            'Vannak-e olyan társadalmi mozgalmak, amelyekbe bekapcsolódhatunk?',
            'Milyen értékváltozások történnek, amelyek támogatják küldetésünket?',
            'Vannak-e olyan fenntarthatósági vagy etikai trendek, amelyekhez illeszkedünk?'
          ]
        },
        {
          name: 'Kapcsolati és Kollaborációs Lehetőségek',
          questions: [
            'Kiket tudnánk támogatni? Hogyan tudnánk őket támogatni?',
            'Hogyan változik a szakmánk? Hogyan használhatjuk ki ezeket a változásokat?',
            'Van-e szükséglet az ügynökségünkben / szervezetünkben, amit senki sem elégít ki?',
            'Mit csinálhatnánk ma, amit még nem csinálunk?',
            'Hogyan alakíthatjuk át gyengeségeinket lehetőségekké?',
            'Vannak-e belső folyamatok, amelyeket újragondolhatunk?'
          ]
        }
      ]
    },
    {
      id: 'threats',
      title: 'VESZÉLYEK',
      icon: '⚠️',
      color: '#9CA3AF',
      gradient: 'from-gray-400 to-gray-500',
      description: 'Külső, negatív tényezők és kockázatok, amelyek veszélyeztethetnek',
      explanation: 'A veszélyek azok a külső, negatív tényezők és kockázatok, amelyek veszélyeztethetik céljaink elérését vagy pozíciónkat.',
      audioUrl: '/audio/threats.mp3',
      categories: [
        {
          name: 'Alapvető Veszély-Kérdések',
          questions: [
            'Milyen piaci trendek vagy változások jelentenek veszélyt számunkra?',
            'Vannak-e új versenytársak, akik belépnek a piacra?',
            'Milyen új funkciók a konkurensektől erodálhatják versenyelőnyünket?',
            'Milyen disruptív technológiák kihívást jelenthetnek?',
            'Vannak-e olyan szabályozási vagy politikai változások, amelyek árthatnak nekünk vagy üzleti modellünknek?',
            'Vannak-e olyan elmozdulások a vásárlói preferenciákban, amelyek a kínálatunk ellen hatnak?',
            'Vannak-e olyan ismétlődő ügyfél/felhasználói visszajelzések, amelyek növekvő lemorzsolódást okozhatnak?'
          ]
        },
        {
          name: 'Gazdasági és Pénzügyi Veszélyek',
          questions: [
            'Befolyásolhatnak-e gazdasági visszaesések üzletünket?',
            'Mi az ellátási lánc megszakadásának kockázata?',
            'Vannak-e potenciális jogi vagy peres veszélyek bármely működésünkből?',
            'Melyek a sebezhetőségek a szellemi tulajdonunkban?',
            'Vannak-e potenciális PR vagy reputációs kockázatok, amelyekkel szembenézünk?',
            'Mik a veszélyek üzletünkre nézve globális vagy geopolitikai kérdésekből?',
            'Vannak-e kockázatok, amelyek jelenlegi partnereinkkel vagy szövetségeseinkkel kapcsolatosak?',
            'Negatívan befolyásolhatnak-e bennünket a növekvő költségek?'
          ]
        },
        {
          name: 'Piaci és Verseny Veszélyek',
          questions: [
            'Milyen akadályokkal szembesülünk?',
            'Melyik gyengeségünk akadályozhatja meg, hogy elérjük céljainkat?',
            'Ki és/vagy mi okozhat problémákat számunkra a jövőben? Hogyan?',
            'Változnak-e olyan szabványok, politikák és/vagy jogszabályok, amelyek negatívan befolyásolhatnak minket?',
            'Versengünk-e másokkal szolgáltatás nyújtásában?'
          ]
        },
        {
          name: 'Technológiai Veszélyek',
          questions: [
            'Vannak-e olyan technológiák, amelyekben szakértők vagyunk, de elavulttá válnak?',
            'Veszélyt jelent-e az automatizáció vagy AI általi helyettesítés?',
            'Vannak-e cybersecurity kockázatok?',
            'Digitális transzformáció miatt lemaradhatunk-e?'
          ]
        },
        {
          name: 'Társadalmi és Demográfiai Veszélyek',
          questions: [
            'Vannak-e generációs különbségek, amelyek kihívást jelentenek?',
            'Milyen kulturális változások árthatnak nekünk?',
            'Demográfiai változások (öregedés/fiatalodás) hátrányosak-e?',
            'Szociális normák átalakulása negatívan hat-e?'
          ]
        },
        {
          name: 'Környezeti és Természeti Veszélyek',
          questions: [
            'Vannak-e környezeti vagy fenntarthatósági veszélyek, amelyeket figyelembe kell vennünk?',
            'Klímaváltozás hatásai veszélyeztetnek-e minket?',
            'Természeti katasztrófák kockázata magas-e a helyszínünkön?'
          ]
        },
        {
          name: 'Belső Szervezeti Veszélyek',
          questions: [
            'Magas-e a munkatársi fluktuáció, ami veszélyeztetheti növekedésünket?',
            'Vannak-e belső konfliktusok, amelyek destabilizálnak?',
            'Megfelelő-e a vezetői utánpótlás tervezésünk?',
            'Vannak-e kritikus függőségek egyetlen személyen vagy erőforráson?'
          ]
        },
        {
          name: 'Személyes Veszélyek',
          questions: [
            'Vannak-e egészségügyi problémák, amelyek korlátoznak?',
            'Veszélyt jelent-e családi krízis vagy kapcsolati probléma?',
            'Földrajzi elhelyezkedésem hátrányos-e?',
            'Kiégés vagy mentális egészség-problémák veszélyeztetnek-e?',
            'Vannak-e pénzügyi sebezhetőségek (pl. hitelek, megtakarítás hiánya)?'
          ]
        },
        {
          name: 'Reputációs és Kommunikációs Veszélyek',
          questions: [
            'Vannak-e félreértések vagy kommunikációs zavarok, amelyek árthatnak reputációnknak?',
            'Közösségi média botrányok kockázata fennáll-e?',
            'Negatív PR vagy rossz hírnév veszélye van-e?',
            'Vannak-e olyan múltbeli akciók, amelyek felszínre kerülhetnek és árthatnak?'
          ]
        },
        {
          name: 'Strukturális és Jogi Veszélyek',
          questions: [
            'Vannak-e olyan compliance követelmények, amelyeket nehéz teljesíteni?',
            'Adózási változások negatívan érintenek-e minket?',
            'Szakmai szabályozások szigorodnak-e?',
            'Vannak-e adatvédelmi és GDPR kockázatok?',
            'Túl későn lépünk-e be egy piacra?',
            'Versenyhátányba kerülünk-e, ha nem cselekszünk gyorsan?'
          ]
        }
      ]
    }
  ];

  const toggleAudio = (quadrantId) => {
    const audio = audioRefs.current[quadrantId];
    if (!audio) return;

    if (playingAudio === quadrantId) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Stop all other audios
      Object.values(audioRefs.current).forEach(a => a && a.pause());
      audio.play();
      setPlayingAudio(quadrantId);
    }
  };

  const openModal = (quadrant) => {
    setModalContent(quadrant);
    setShowModal(true);
    setExpandedCategories({});
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
    setExpandedCategories({});
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Load answers from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('swotAnswers');
    const savedHidden = localStorage.getItem('swotHiddenQuestions');
    if (saved) {
      try {
        setSwotAnswers(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load saved answers:', e);
      }
    }
    if (savedHidden) {
      try {
        setHiddenQuestions(JSON.parse(savedHidden));
      } catch (e) {
        console.error('Failed to load hidden questions:', e);
      }
    }
  }, []);

  // Auto-save answers to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('swotAnswers', JSON.stringify(swotAnswers));
    }, 500);
    return () => clearTimeout(timer);
  }, [swotAnswers]);

  // Auto-save hidden questions
  useEffect(() => {
    localStorage.setItem('swotHiddenQuestions', JSON.stringify(hiddenQuestions));
  }, [hiddenQuestions]);

  const updateAnswer = (quadrantId, questionKey, value) => {
    setSwotAnswers(prev => ({
      ...prev,
      [quadrantId]: {
        ...prev[quadrantId],
        [questionKey]: value
      }
    }));
  };

  const toggleQuestionVisibility = (quadrantId, questionKey) => {
    setHiddenQuestions(prev => ({
      ...prev,
      [quadrantId]: {
        ...prev[quadrantId],
        [questionKey]: !prev[quadrantId]?.[questionKey]
      }
    }));
  };

  const exportToPDF = () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Add Comfortaa font to jsPDF
      pdf.addFileToVFS('Comfortaa-Regular.ttf', ComfortaaFont);
      pdf.addFont('Comfortaa-Regular.ttf', 'Comfortaa', 'normal');
      pdf.setFont('Comfortaa');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Helper function to check if new page is needed
      const checkNewPage = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Add title with better formatting
      pdf.setFontSize(26);
      pdf.setFont('Comfortaa', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.text('SWOT Elemzés', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Add date
      pdf.setFontSize(10);
      pdf.setFont('Comfortaa', 'normal');
      pdf.setTextColor(100, 100, 100);
      const date = new Date().toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      pdf.text(date, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Add each quadrant
      quadrants.forEach((quadrant, quadIdx) => {
        // Check if we have any content for this quadrant
        let hasContent = false;
        quadrant.categories.forEach((category, catIdx) => {
          category.questions.forEach((question, qIdx) => {
            const questionKey = `${catIdx}-${qIdx}`;
            const answer = swotAnswers[quadrant.id]?.[questionKey];
            const isHidden = hiddenQuestions[quadrant.id]?.[questionKey];
            if (!isHidden && answer) {
              hasContent = true;
            }
          });
        });

        // Skip quadrant if no content
        if (!hasContent) return;

        // New page for each quadrant (except first)
        if (quadIdx > 0) {
          pdf.addPage();
          yPosition = margin;
        }

        // Quadrant title with icon alternative (emoji as text)
        checkNewPage(20);
        pdf.setFontSize(20);
        pdf.setFont('Comfortaa', 'normal');
        pdf.setTextColor(127, 168, 147);
        pdf.text(quadrant.title, margin, yPosition);
        yPosition += 10;

        // Draw line under title
        pdf.setDrawColor(127, 168, 147);
        pdf.setLineWidth(0.8);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;

        // Reset text color
        pdf.setTextColor(0, 0, 0);

        // Add each category
        quadrant.categories.forEach((category, catIdx) => {
          // Check if category has any content
          let categoryHasContent = false;
          category.questions.forEach((question, qIdx) => {
            const questionKey = `${catIdx}-${qIdx}`;
            const answer = swotAnswers[quadrant.id]?.[questionKey];
            const isHidden = hiddenQuestions[quadrant.id]?.[questionKey];
            if (!isHidden && answer) {
              categoryHasContent = true;
            }
          });

          if (!categoryHasContent) return;

          // Check if we need a new page for category
          checkNewPage(15);

          // Category title
          pdf.setFontSize(14);
          pdf.setFont('Comfortaa', 'normal');
          pdf.setTextColor(60, 60, 60);
          pdf.text(category.name, margin, yPosition);
          yPosition += 8;

          // Add questions and answers
          category.questions.forEach((question, qIdx) => {
            const questionKey = `${catIdx}-${qIdx}`;
            const answer = swotAnswers[quadrant.id]?.[questionKey];
            const isHidden = hiddenQuestions[quadrant.id]?.[questionKey];

            if (isHidden || !answer) return;

            // Check if we need a new page
            checkNewPage(20);

            // Question
            pdf.setFontSize(11);
            pdf.setFont('Comfortaa', 'normal');
            pdf.setTextColor(80, 80, 80);
            const questionLines = pdf.splitTextToSize(question, contentWidth - 8);
            pdf.text(questionLines, margin + 4, yPosition);
            yPosition += questionLines.length * 5 + 2;

            // Answer
            pdf.setFontSize(10);
            pdf.setFont('Comfortaa', 'normal');
            pdf.setTextColor(0, 0, 0);
            const answerLines = pdf.splitTextToSize(answer, contentWidth - 8);
            pdf.text(answerLines, margin + 4, yPosition);
            yPosition += answerLines.length * 4.5 + 7;
          });

          yPosition += 3;
        });

        yPosition += 8;
      });

      // Add footer on last page
      pdf.setFontSize(8);
      pdf.setFont('Comfortaa', 'normal');
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        'Készítette: SWOT Elemzés Alkalmazás',
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      pdf.save('SWOT-Elemzes.pdf');
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Hiba történt a PDF létrehozása során');
    }
  };

  const handleSeek = (quadrantId, value) => {
    const audio = audioRefs.current[quadrantId];
    if (audio) {
      audio.currentTime = value;
      setAudioProgress(prev => ({ ...prev, [quadrantId]: value }));
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleGuideAudio = () => {
    const audio = guideAudioRef.current;
    if (!audio) return;

    if (isGuideAudioPlaying) {
      audio.pause();
      setIsGuideAudioPlaying(false);
    } else {
      audio.play();
      setIsGuideAudioPlaying(true);
    }
  };

  const handleGuideAudioSeek = (value) => {
    const audio = guideAudioRef.current;
    if (audio) {
      audio.currentTime = value;
      setGuideAudioProgress(value);
    }
  };

  useEffect(() => {
    // Initialize audio elements
    const audioElements = {};

    quadrants.forEach(q => {
      const audio = new Audio();
      if (q.audioUrl) {
        audio.src = q.audioUrl;

        audio.addEventListener('loadedmetadata', () => {
          setAudioDuration(prev => ({ ...prev, [q.id]: audio.duration }));
        });

        audio.addEventListener('timeupdate', () => {
          setAudioProgress(prev => ({ ...prev, [q.id]: audio.currentTime }));
        });

        audio.addEventListener('ended', () => {
          setPlayingAudio(null);
          setAudioProgress(prev => ({ ...prev, [q.id]: 0 }));
        });
      }
      audioRefs.current[q.id] = audio;
      audioElements[q.id] = audio;
    });

    return () => {
      Object.values(audioElements).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Initialize guide audio
    const audio = new Audio('/hasznalati-utmutato.m4a');
    guideAudioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setGuideAudioDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      setGuideAudioProgress(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsGuideAudioPlaying(false);
      setGuideAudioProgress(0);
    });

    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900">
              SWOT Elemzés
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* SWOT Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {quadrants.map((quadrant, index) => (
            <div
              key={quadrant.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
            >
              {/* Card Header */}
              <div
                className="bg-gradient-to-r from-[#7FA893] to-[#6B9080] p-6 text-white"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{quadrant.icon}</span>
                    <div>
                      <h3 className="text-2xl font-bold">{quadrant.title}</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold opacity-50">
                    {index + 1}
                  </div>
                </div>
                <p className="text-sm opacity-95">{quadrant.description}</p>
              </div>

              {/* Card Body */}
              <div className="p-6 flex flex-col flex-grow">
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  {quadrant.explanation}
                </p>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(quadrant);
                    }}
                    className="w-full py-3 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Lightbulb className="w-5 h-5" />
                    Kérdések Megtekintése
                  </button>

                  {quadrant.audioUrl && (
                    <div className="w-full bg-gray-100 rounded-xl p-3 flex items-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAudio(quadrant.id);
                        }}
                        className={`
                          p-2 rounded-lg flex-shrink-0
                          ${playingAudio === quadrant.id
                            ? 'bg-gray-800 text-white hover:bg-gray-700'
                            : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                          }
                          transition-all duration-200
                        `}
                      >
                        {playingAudio === quadrant.id ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>

                      <div className="flex-grow flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max={audioDuration[quadrant.id] || 0}
                          value={audioProgress[quadrant.id] || 0}
                          onChange={(e) => handleSeek(quadrant.id, parseFloat(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-grow h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-gray-600"
                        />
                        <span className="text-xs text-gray-600 flex-shrink-0 font-mono min-w-[70px] text-right">
                          {formatTime(audioProgress[quadrant.id] || 0)} / {formatTime(audioDuration[quadrant.id] || 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

      </main>

      {/* Modal for Questions */}
      {showModal && modalContent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className={`bg-gradient-to-r ${modalContent.gradient} p-6 text-white`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{modalContent.icon}</span>
                  <div>
                    <h2 className="text-3xl font-bold">{modalContent.title}</h2>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <p className="text-gray-700 leading-relaxed">
                  {modalContent.explanation}
                </p>
              </div>

              {/* Categories with Expandable Questions */}
              <div className="space-y-4">
                {modalContent.categories.map((category, catIndex) => (
                  <div
                    key={catIndex}
                    className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category.name)}
                      className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: modalContent.color }}
                        >
                          {catIndex + 1}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {category.name}
                        </h3>
                        <span className="text-sm text-gray-500">
                          ({category.questions.length} kérdés)
                        </span>
                      </div>
                      {expandedCategories[category.name] ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>

                    {/* Category Questions - Expandable */}
                    {expandedCategories[category.name] && (
                      <div className="p-6 bg-white">
                        <ul className="space-y-3">
                          {category.questions.map((question, qIndex) => (
                            <li
                              key={qIndex}
                              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <span className="text-gray-400 font-medium flex-shrink-0 mt-1">
                                {qIndex + 1}.
                              </span>
                              <p className="text-gray-700">{question}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="w-full px-6 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-700 transition-all shadow-md"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Buttons */}
      {!showEditor && (
        <div className="fixed bottom-8 right-4 sm:right-8 z-40 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={() => setShowGuide(true)}
            className="bg-gradient-to-r from-[#7FA893] to-[#6B9080] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2 sm:gap-3 font-bold text-sm sm:text-lg whitespace-nowrap"
            title="Használati Útmutató"
          >
            <BookOpen className="w-5 h-5 sm:w-7 sm:h-7 flex-shrink-0" />
            <span className="hidden sm:inline">Használati Útmutató</span>
            <span className="sm:hidden">Útmutató</span>
          </button>
          <button
            onClick={() => setShowEditor(true)}
            className="bg-gradient-to-r from-[#7FA893] to-[#6B9080] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2 sm:gap-3 font-bold text-sm sm:text-lg whitespace-nowrap"
            title="SWOT Dokumentáló"
          >
            <FileText className="w-5 h-5 sm:w-7 sm:h-7 flex-shrink-0" />
            <span className="hidden sm:inline">SWOT Elemzés Készítő</span>
            <span className="sm:hidden">SWOT Készítő</span>
          </button>
        </div>
      )}

      {/* Editor Modal - Simplified without drag & drop */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop with blur */}
          <div
            className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
            onClick={() => !isEditorMinimized && setShowEditor(false)}
          />

          {/* Editor Panel */}
          <div
            className={`relative bg-white rounded-2xl shadow-2xl flex flex-col transform transition-all duration-300 ${
              isEditorMinimized ? 'w-auto max-w-2xl' : 'w-full max-w-5xl max-h-[90vh]'
            }`}
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Header with tabs */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#7FA893]" />
                  SWOT Dokumentáló
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditorMinimized(!isEditorMinimized)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title={isEditorMinimized ? "Maximalizálás" : "Minimalizálás"}
                  >
                    <Minimize2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      setShowEditor(false);
                      setIsEditorMinimized(false);
                    }}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Quadrant Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {quadrants.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => setSelectedEditorQuadrant(q.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all
                      ${selectedEditorQuadrant === q.id
                        ? 'bg-gradient-to-r ' + q.gradient + ' text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <span>{q.icon}</span>
                    <span>{q.title}</span>
                  </button>
                ))}
              </div>

              {/* Audio Player - Always visible */}
              {quadrants.find(q => q.id === selectedEditorQuadrant)?.audioUrl && (
                <div className="mt-3 w-full bg-white rounded-xl p-3 flex items-center gap-3 shadow-sm">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAudio(selectedEditorQuadrant);
                    }}
                    className={`
                      p-2 rounded-lg flex-shrink-0
                      ${playingAudio === selectedEditorQuadrant
                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                        : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                      }
                      transition-all duration-200
                    `}
                  >
                    {playingAudio === selectedEditorQuadrant ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  <div className="flex-grow flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max={audioDuration[selectedEditorQuadrant] || 0}
                      value={audioProgress[selectedEditorQuadrant] || 0}
                      onChange={(e) => handleSeek(selectedEditorQuadrant, parseFloat(e.target.value))}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-grow h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-gray-600"
                    />
                    <span className="text-xs text-gray-600 flex-shrink-0 font-mono min-w-[70px] text-right">
                      {formatTime(audioProgress[selectedEditorQuadrant] || 0)} / {formatTime(audioDuration[selectedEditorQuadrant] || 0)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Questions and Answers Area - Hidden when minimized */}
            {!isEditorMinimized && (
              <div className="flex-1 overflow-y-auto p-6">
                {quadrants.find(q => q.id === selectedEditorQuadrant)?.categories.map((category, catIdx) => (
                  <div key={catIdx} className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      {category.name}
                    </h3>
                    <div className="space-y-4">
                      {category.questions.map((question, qIdx) => {
                        const questionKey = `${catIdx}-${qIdx}`;
                        const isHidden = hiddenQuestions[selectedEditorQuadrant]?.[questionKey];

                        if (isHidden) return null;

                        return (
                          <div key={qIdx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-gray-700 font-medium flex-grow">{question}</p>
                              <button
                                onClick={() => toggleQuestionVisibility(selectedEditorQuadrant, questionKey)}
                                className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                                title="Kérdés elrejtése"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <textarea
                              value={swotAnswers[selectedEditorQuadrant]?.[questionKey] || ''}
                              onChange={(e) => updateAnswer(selectedEditorQuadrant, questionKey, e.target.value)}
                              placeholder="Írja be a választ..."
                              className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7FA893] focus:border-transparent resize-none min-h-[80px] text-gray-800"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer with actions - Hidden when minimized */}
            {!isEditorMinimized && (
              <div className="border-t border-gray-200 p-4 bg-gray-50 flex gap-3">
                <button
                  onClick={exportToPDF}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#7FA893] to-[#6B9080] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <Download className="w-5 h-5" />
                  PDF Mentés
                </button>
                <button
                  onClick={() => setShowEditor(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                >
                  Bezárás
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Guide Modal */}
      {showGuide && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowGuide(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Guide Header */}
            <div className="bg-gradient-to-r from-[#7FA893] to-[#6B9080] p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-8 h-8" />
                  <h2 className="text-3xl font-bold">Használati Útmutató</h2>
                </div>
                <button
                  onClick={() => setShowGuide(false)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Guide Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gradient-to-r from-[#7FA893] to-[#6B9080] rounded-2xl p-8 mb-6">
                <h3 className="text-2xl font-bold text-white mb-4">SWOT Elemzés Kitöltési Útmutató</h3>
                <p className="text-white text-lg mb-6">
                  Hallgassa meg a részletes útmutatót a SWOT elemzés kitöltéséhez!
                </p>

                {/* Audio Player */}
                <div className="bg-white rounded-xl p-4 flex items-center gap-4">
                  <button
                    onClick={toggleGuideAudio}
                    className={`
                      p-4 rounded-lg flex-shrink-0
                      ${isGuideAudioPlaying
                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                        : 'bg-[#7FA893] text-white hover:bg-[#6B9080]'
                      }
                      transition-all duration-200
                    `}
                  >
                    {isGuideAudioPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6" />
                    )}
                  </button>

                  <div className="flex-grow flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max={guideAudioDuration || 0}
                      value={guideAudioProgress || 0}
                      onChange={(e) => handleGuideAudioSeek(parseFloat(e.target.value))}
                      className="flex-grow h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-[#7FA893]"
                    />
                    <span className="text-sm text-gray-600 flex-shrink-0 font-mono min-w-[90px] text-right">
                      {formatTime(guideAudioProgress || 0)} / {formatTime(guideAudioDuration || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="prose max-w-none">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Főbb lépések:</h4>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>1. Hallgassa meg a hanganyagot</strong> - Minden quadrant rendelkezik saját instrukciós hanganyaggal</li>
                  <li><strong>2. Válaszoljon a kérdésekre</strong> - Kattintson a "SWOT Elemzés Készítő" gombra</li>
                  <li><strong>3. Töltse ki a válaszokat</strong> - Minden kérdésre adjon részletes választ</li>
                  <li><strong>4. Mentse PDF-be</strong> - A kitöltött elemzést letöltheti PDF formátumban</li>
                </ul>

                <h4 className="text-xl font-bold text-gray-900 mt-6 mb-4">SWOT kvadránsok:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-800 mb-2">💪 ERŐSSÉGEK</h5>
                    <p className="text-sm text-gray-600">Belső pozitív tényezők</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h5 className="font-bold text-red-800 mb-2">⚠️ GYENGESÉGEK</h5>
                    <p className="text-sm text-gray-600">Belső negatív tényezők</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h5 className="font-bold text-blue-800 mb-2">🚀 LEHETŐSÉGEK</h5>
                    <p className="text-sm text-gray-600">Külső pozitív tényezők</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h5 className="font-bold text-yellow-800 mb-2">⚡ VESZÉLYEK</h5>
                    <p className="text-sm text-gray-600">Külső negatív tényezők</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Guide Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowGuide(false);
                  if (isGuideAudioPlaying) {
                    toggleGuideAudio();
                  }
                }}
                className="w-full px-6 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-700 transition-all shadow-md"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <p className="text-gray-600 text-sm">
          © 2025 SWOT Elemzés Alkalmazás
        </p>
      </footer>
    </div>
  );
}
